import { addMinutes, format } from 'date-fns'
import {
  openingHourMatchesSchedule,
  type OpeningHour,
  type DurationByPeople,
  type OpeningHourSchedule,
} from '@/lib/types/openingHour'

/**
 * The onboarding wizard stores opening hours with:
 * 1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday
 *
 * JS Date.getDay() returns 0=Sunday, 1=Monday, ..., 6=Saturday.
 * This function converts JS day to our stored day number.
 */
export function jsDateDayToStored(jsDay: number): number {
  // 0 (Sunday) → 7, 1-6 (Mon–Sat) → 1-6 unchanged
  return jsDay === 0 ? 7 : jsDay
}

/**
 * Returns the set of stored day numbers (1–7) that have at least one opening hour.
 */
export function getOpenStoredDays(
  openingHours: OpeningHour[],
  schedule?: OpeningHourSchedule,
): Set<number> {
  const days = new Set<number>()
  for (const oh of openingHours) {
    if (!oh.special && (!schedule || openingHourMatchesSchedule(oh, schedule))) days.add(oh.day)
  }
  return days
}

/**
 * Returns true if the given Date is a day the restaurant is open.
 */
export function isDateOpen(
  date: Date,
  openingHours: OpeningHour[],
  schedule?: OpeningHourSchedule,
): boolean {
  const storedDay = jsDateDayToStored(date.getDay())
  return openingHours.some(
    (oh) => !oh.special && oh.day === storedDay && (!schedule || openingHourMatchesSchedule(oh, schedule)),
  )
}

/**
 * Given a selected date (YYYY-MM-DD string), find all opening hours that apply to that day.
 */
export function getOpeningHoursForDate(
  openingHours: OpeningHour[],
  dateStr: string,
  schedule?: OpeningHourSchedule,
): OpeningHour[] {
  const date = new Date(dateStr)
  const storedDay = jsDateDayToStored(date.getDay())
  return openingHours.filter(
    (oh) => !oh.special && oh.day === storedDay && (!schedule || openingHourMatchesSchedule(oh, schedule)),
  )
}

/**
 * Converts HHMM integer (e.g. 1130 → "11:30", 900 → "09:00") to "HH:MM" string.
 */
function hhmmToTime(hhmm: number): { h: number; m: number } {
  return { h: Math.floor(hhmm / 100), m: hhmm % 100 }
}

/**
 * Given an OpeningHour record, compute available booking time slots as "HH:MM" strings.
 *
 * Uses seatingInterval for slot spacing (default 30 min), and ensures the last
 * slot ends before closing time (open + seatingDuration <= close).
 */
export function getSlotsFromOpeningHour(oh: OpeningHour): string[] {
  const interval = oh.seatingInterval > 0 ? oh.seatingInterval : 30
  const duration = oh.seatingDuration > 0 ? oh.seatingDuration : 90

  const { h: openH, m: openM } = hhmmToTime(oh.open)
  const { h: closeH, m: closeM } = hhmmToTime(oh.close)

  // Build a base date for iteration (date doesn't matter, only time)
  const base = new Date()
  base.setHours(openH, openM, 0, 0)

  const closeDate = new Date()
  closeDate.setHours(closeH, closeM, 0, 0)

  // Last valid slot start: closing time minus booking duration
  const latestStart = new Date(closeDate.getTime() - duration * 60 * 1000)

  const slots: string[] = []
  let current = new Date(base)

  while (current <= latestStart) {
    slots.push(format(current, 'HH:mm'))
    current = addMinutes(current, interval)
  }

  return slots
}

/**
 * Compute all time slots for a given date across all applicable opening hours.
 * Deduplicates and sorts slots.
 */
export function getSlotsForDate(
  openingHours: OpeningHour[],
  dateStr: string,
  schedule?: OpeningHourSchedule,
): string[] {
  const ohs = getOpeningHoursForDate(openingHours, dateStr, schedule)
  const allSlots = new Set<string>()
  for (const oh of ohs) {
    for (const slot of getSlotsFromOpeningHour(oh)) {
      allSlots.add(slot)
    }
  }
  return Array.from(allSlots).sort()
}

/**
 * Determine booking duration for a given opening hour and party size.
 */
export function getDurationForParty(oh: OpeningHour, people: number): number {
  if (oh.useDurationByPeople && Array.isArray(oh.durationByPeople) && oh.durationByPeople.length > 0) {
    const sorted = [...oh.durationByPeople].sort(
      (a: DurationByPeople, b: DurationByPeople) => b.people - a.people,
    )
    const rule = sorted.find((r: DurationByPeople) => people >= r.people)
    if (rule) return rule.duration
  }
  return oh.seatingDuration > 0 ? oh.seatingDuration : 90
}
